// cmds/ScoringCmds.js
/**
 * @fileoverview
 *
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {import('BMapsModel').CaseData} CaseData
 * @typedef {import('BMapsModel').Residue} Residue
 * @typedef {import('BMapsModel').MapCase} MapCase
 *
 * @typedef ScoringCmds
 * @type {object}
 * @property {scoreCompounds} ScoreCompounds
 */
import { App } from 'BMapsSrc/BMapsApp';
import { UserActions, UserCmd } from 'BMapsCmds/UserCmd';
import { DockingReference } from 'BMapsSrc/data_tools';
import { pointsCentroid, pointDistanceSquared } from 'BMapsUtil/atom_distance_utils';
import { ensureArray } from 'BMapsUtil/js_utils';
import { MoleculeLoadOptions, MolDataSource } from 'BMapsSrc/utils';
import { gatherEnergyErrors } from 'BMapsCmds/cmds_common';
import { fetchLID } from 'BMapsUI/ProLIFApi';
import { BindingSite, Compound } from 'BMapsModel';
import { PdbData } from 'BMapsSrc/util/pdb';
import { getPdbDataForResidues } from 'BMapsUtil/pdb_export';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { withPausedRedisplay } from 'BMapsSrc/util/display_utils';
import { getPreference } from 'BMapsRedux/prefs/access';

/** @type {ScoringCmds} */
export const ScoringCmds = {
    ScoreCompounds: new UserCmd('ScoreCompounds', scoreCompounds),
    // noEffect: a read (build the request, return the diagram; stores nothing). With ProLIF pi-pi
    // detection (the default) the pi-pi display calls it on every redraw, so without this, an undo
    // that repaints discarded the redo it had just made possible. On the lab-only server PDB route
    // its nested Select calls still do; see the register, Slice C item 4c, "Found while checking".
    FetchProLifData: new UserCmd('FetchProLifData', fetchProLifData, { noEffect: true }),
    GetProteinPdb: new UserCmd('GetProteinPdb', getProteinPdb),
};

/* IMPLEMENTATION */

/**
 *
 * @param {{ compounds: Compound[], proteins: MapCase[], scoringParams: object }}
 * @return {Promise<{
 * results: Map<MapCase, { topPose: Compound, topScore: number, errors: string[] }
 * errors: {name: string, message: string}[]
 * }>>}
 */
/**
 * The scoring settings that live INSIDE `scoringParams`, and which `score-compounds` declares
 * as flat parameters because a plan cannot carry an object -- `'object'` is not in
 * PLAN_LITERAL_TYPES (gibbs/registry/types.js), so `scoringParams` can never be supplied by a
 * plan while these five can.
 *
 * Nothing unpacked them. The ActionDef declared all five, the dispatcher put them at the top
 * level of the options object, and this callee destructured only `scoringParams` -- so a plan
 * asking for `quick: true` got the full 10-pose AutoDock pass and no indication otherwise.
 */
export const SCORING_PARAM_KEYS = Object.freeze([
    'quick', 'diffdockPoseCount', 'autodockPoseCount', 'autodockSpeed', 'cleanup',
]);

/** The scoring settings supplied flat, keyed as `scoringParams` expects them. Exported for its
 * unit test: this folding is the whole of the fix and is worth asserting directly. */
export function flatScoringParams(source) {
    const out = {};
    for (const key of SCORING_PARAM_KEYS) {
        if (source[key] !== undefined) out[key] = source[key];
    }
    return out;
}

/**
 * The MapCases to score against.
 *
 * Two shapes arrive here. The Scoring side panel passes MapCases, which is what the loop below
 * needs. A PLAN cannot name a MapCase -- the dispatcher has no resolver for that type -- so
 * `score-compounds` declares its proteins as CaseData, and `allCaseData()` yields CaseData
 * objects. Feeding one of those to `Workspace.lookupCaseData` THROWS, by design, and that exact
 * confusion already cost a misdiagnosis on this tree (see the error text there).
 *
 * Supplying neither is also legitimate from a plan: selectivity scoring means "against the
 * proteins that are loaded", which is what the fork defaulted to.
 */
export function scoringTargets(proteins) {
    const supplied = proteins == null ? [] : [].concat(proteins).filter(Boolean);
    const list = supplied.length > 0 ? supplied : App.Workspace.getLoadedProteins();
    // A CaseData carries its MapCase; a MapCase does not have a `mapCase` property.
    return list.map((item) => item.mapCase ?? item).filter(Boolean);
}

async function scoreCompounds({
    compounds, proteins, scoringParams: scoringParamsIn = {}, ...flatParams
}) {
    const scoringParams = { ...scoringParamsIn, ...flatScoringParams(flatParams) };
    const targets = scoringTargets(proteins);
    /** @type {Map<MapCase, {topPose: Compound, topScore: number}} */
    const results = new Map();
    const errors = [];

    // take a snap shot of what compounds are currently loaded before scoring.
    const originalCmpds = [...App.Workspace.getLoadedCompounds()];

    // Ensure all compounds exist on all proteins
    await loopOnProteins(targets, async (mapCase) => {
        const caseData = App.Workspace.lookupCaseData(mapCase);
        for (const compound of compounds) {
            await ensureCompoundOnProtein(compound, caseData);
        }
    });

    // Score the compounds on the proteins
    await loopOnProteins(targets, async (mapCase) => {
        for (const compound of compounds) {
            const score = await scoreCompoundForOne({ compound, mapCase, scoringParams });
            // If an error causes a compound to not get scored add it to the errors array.
            if (!score.topPose || !score.topScore) {
                errors.push(
                    `Unable to score ${compound.displayName} on ${mapCase.getShortName()}`,
                    ...score.errors
                );
                continue;
            }
            // If the compound still scores even though
            // it had an error just add the error to results.
            let perCompoundMap = results.get(compound);
            if (!perCompoundMap) {
                perCompoundMap = new Map();
                results.set(compound, perCompoundMap);
            }

            perCompoundMap.set(mapCase, score);
        }
    });

    // Cleanup
    const toRemove = [];
    const toGetEnergies = [];
    for (const perCompoundMap of results.values()) {
        for (const score of perCompoundMap.values()) {
            for (const pose of score.scoringPoses) {
                if (originalCmpds.includes(pose)) continue; // don't clean up preexisting cmpds
                const keepDockingSites = false; // Can enable for debugging, to see docking refs
                const isDockingSite = score.dockingSites.some((site) => site.pose === pose);
                const keepDockingSite = keepDockingSites && isDockingSite;
                if (pose !== score.topPose && !keepDockingSite) {
                    console.log(`Removing pose ${pose.resSpec} with score ${scorePose(pose).toFixed(2)}, in favor of ${score.topPose.resSpec} with score ${score.topScore.toFixed(2)}`);
                    toRemove.push(pose);
                } else {
                    toGetEnergies.push(pose);
                }
            }
        }
    }

    if (scoringParams.cleanup) {
        for (const pose of toRemove) {
            await UserActions.RemoveCompound(pose);
        }
    }
    // Solvation is not initially calculated, so fetch it for top poses.
    for (const pose of toGetEnergies) await UserActions.GetEnergies(pose);

    return { results, errors };
}

/**
 * This function is to unify calls for both old (>1 connection) and new (1 connection) selectivity.
 * If we have multiple connections, we can take advantage of Promise.all.
 * For a single connection, the server will eventually crash if we try to do too many things in a
 * Promise.all.
 *
 * This function gathers proteins into groups by connection.
 * It does Promise.all across each conn. group, but awaits in a loop for proteins within the group.
 * @param {MapCase[]} proteins
 * @param {function} doFn
 */
async function loopOnProteins(proteins, doFn) {
    const connectionProteinsMap = new Map();
    for (const mapCase of proteins) {
        const { connector } = App.getDataParents(mapCase);
        let connProteinList = connectionProteinsMap.get(connector);
        if (!connProteinList) {
            connProteinList = [];
            connectionProteinsMap.set(connector, connProteinList);
        }
        connProteinList.push(mapCase);
    }

    await Promise.all(
        [...connectionProteinsMap.values()].map(async (proteinList) => {
            for (const protein of proteinList) {
                await doFn(protein);
            }
        })
    );
}

/**
 * @param {{
 * compound: Compound,
 * mapCase: MapCase,
 * scoringParams:{}
 * }} param0
 * @returns
 */
async function scoreCompoundForOne({ compound, mapCase, scoringParams }) {
    const scoringResults = await getScoringCandidates(compound, mapCase, scoringParams);

    const { compounds: scoringPoses, dockingSites=[], errors } = scoringResults;

    scoringPoses.sort(scoreSortFn);

    const topPose = scoringPoses[0];

    const topScore = scorePose(topPose);

    console.log(`Top pose for ${compound.resSpec} against ${mapCase.getNodeName()}: ${topPose?.resSpec}, score ${topScore.toFixed(2)}`);

    return {
        topPose,
        topScore,
        errors,
        scoringPoses,
        dockingSites,
    };
}

/**
 * For a compound and a mapCase, return poses that will be scored, to identify the top pose.
 */
async function getScoringCandidates(compound, mapCase, scoringParams) {
    const caseDataP = App.Workspace.lookupCaseData(mapCase);

    /** @type {Compound[]} */
    const compounds = [];
    const errors = [];

    const { diffdockPoseCount, autodockPoseCount, autodockSpeed } = getScoringParams(scoringParams);

    // If scoring for a different protein, need to copy the compound to the new protein.
    const ensureResults = await ensureCompoundOnProtein(compound, caseDataP);
    const { compound: dockingCompound, errors: ensureErrs } = ensureResults;
    if (ensureErrs) errors.push(...ensureErrs);

    if (dockingCompound) {
        // Ok to consider the docking compound as a scoring candidate?
        compounds.push(dockingCompound);
    } else {
        // Nothing we can do if we can't get the compound onto the target protein
        return { compounds, errors };
    }

    // Start with DiffDock to get pose candidates and docking references for autodock
    const diffdockParams = { dockingProgram: 'diffdock', poseCount: diffdockPoseCount };
    const diffdockResults = await dockAndMinimizeForScoring(dockingCompound, diffdockParams);
    const { compounds: diffdockPoses, errors: diffdockErrors } = diffdockResults;

    compounds.push(...diffdockPoses);
    errors.push(...diffdockErrors);

    // Autodock
    const dockingSites = getDockingSites(caseDataP, diffdockPoses);

    await Promise.all(dockingSites.map(async (dockingSite) => {
        const { dockingReference } = dockingSite;
        const autodockParams = {
            dockingProgram: 'autodock',
            speedArg: autodockSpeed,
            poseCount: autodockPoseCount,
            boxParams: { refObj: dockingReference },
        };
        const autodockResults = await dockAndMinimizeForScoring(dockingCompound, autodockParams);
        const { compounds: autodockPoses, errors: autodockErrors } = autodockResults;
        compounds.push(...autodockPoses);
        errors.push(...autodockErrors);
    }));

    return { compounds, dockingSites, errors };
}

function getScoringParams(scoringParams={}) {
    const ret = {
        diffdockPoseCount: 10,
        autodockPoseCount: 10,
        autodockSpeed: 'accurate',
    };

    if (scoringParams.quick) {
        ret.diffdockPoseCount = 2;
        ret.autodockPoseCount = 2;
        ret.autodockSpeed = 'faster';
    }
    if (scoringParams.diffdockPoseCount) ret.diffdockPoseCount = scoringParams.diffdockPoseCount;
    if (scoringParams.autodockPoseCount) ret.autodockPoseCount = scoringParams.autodockPoseCount;
    if (scoringParams.autodockSpeed) ret.autodockSpeed = scoringParams.autodockSpeed;
    return ret;
}

/**
 * @param {Compound} compound
 * @param {CaseData} caseDataP
 * @returns {Promise<{compound: Compound} | {errors: string[]}>}
 */
async function ensureCompoundOnProtein(compound, caseDataP) {
    const compoundKey = compound.getUniqueStructId();

    const lookupCompounds = App.Workspace.getLoadedCompounds().filter((pose) => {
        const poseKey = pose.getUniqueStructId();
        return (poseKey === compoundKey && App.getDataParents(pose).mapCase === caseDataP.mapCase);
    });

    if (lookupCompounds.length > 0) {
        return { compound: lookupCompounds[0] };
    }

    const molSource = new MolDataSource({
        sourceType: MolDataSource.Types.ScoreCompound,
        molFormat: 'mol',
        molData: compound.getMol2000(),
    });
    // Note: this MolDataSource usage will not copy the properties or heritage from compound.
    // Ideally, we could assign sourceCmpds, which would cause it to get copied over after loading.
    // However, the mechanism in ConnectedDataCmds currently assumes coordinates were preserved,
    // and for scoring we need to put the new compound "outside the protein" to avoid clashing.
    const { compounds, errors } = await UserActions.LoadMolData(
        molSource,
        new MoleculeLoadOptions(),
        caseDataP,
    );
    return (errors?.length > 0) ? { errors } : { compound: compounds[0] };
}

function getDockingSites(caseDataProtein, diffdockPoses) {
    const sites = [];
    const siteSeparationDistanceSq = 8**2;
    for (const pose of diffdockPoses) {
        const centroid = pointsCentroid(pose.getAtoms());
        if (sites.every(
            ({ centroid: site }) => pointDistanceSquared(centroid, site) > siteSeparationDistanceSq
        )) {
            sites.push({
                pose,
                centroid,
                dockingReference: DockingReference.ForCompound(pose),
            });
        }
    }

    console.log(`ScoreCompound: docking sites: ${sites.map((s) => `${s.pose.resSpec}:${s.centroid.map((x) => x.toFixed(1))}`).join('; ')}`);
    return sites;
}

function scorePose(pose) {
    return pose?.energyInfo.getEnergyScore() || 100;
}

function scoreSortFn(a, b) {
    return scorePose(a) - scorePose(b);
}

async function dockAndMinimize(cmpds, dockingParams, { otherDockOptions, minimizationOptions }={}) {
    const compounds = ensureArray(cmpds);
    const {
        compounds: dockPoses, errors: dockErrs, groups,
    } = await UserActions.SubmitDocking(compounds, dockingParams, otherDockOptions);
    const minResults = await UserActions.EnergyMinimize(dockPoses, minimizationOptions);
    const { compounds: minPoses, errors: rawMinErrs, energies } = minResults;
    minPoses.sort(scoreSortFn);
    const cmpdErrMap = gatherEnergyErrors(
        dockPoses, minPoses, energies,
        rawMinErrs, minimizationOptions
    );
    const minErrs = [];
    for (const [cmpd, cmpdErrs] of cmpdErrMap.entries()) {
        if (cmpdErrs.length === 0) continue;
        for (const cmpdErr of cmpdErrs) {
            minErrs.push(`${cmpd.displayName} energy error: ${cmpdErr}`);
        }
    }
    console.log(`ScoreCompound: docked and minimized: ${minPoses.map((p) => `${p.resSpec}:${scorePose(p)}`).join('; ')}`);
    return { compounds: minPoses, groups, errors: [...dockErrs, ...minErrs] };
}

async function dockAndMinimizeForScoring(cmpds, dockingParams,
    { otherDockOptions: otherDockOptionsIn, minimizationOptions: minimizationOptionsIn }={}) {
    const otherDockOptions = { skipGrouping: true, ...otherDockOptionsIn };
    const minimizationOptions = {
        skipSolvation: true,
        ...minimizationOptionsIn,
    };
    return dockAndMinimize(cmpds, dockingParams, { otherDockOptions, minimizationOptions });
}

/** Diagram dimensions this accepts: a CSS length, or a pixel count to turn into one. */
export const LID_DIMENSION_KEYS = Object.freeze(['width', 'height']);

/** A CSS length for `value`: a number is a pixel count, a string is already one. */
function cssLength(value) {
    return typeof value === 'number' ? `${Math.round(value)}px` : value;
}

/**
 * The dimension fields of `flat`, as CSS lengths, ignoring anything else it carries.
 *
 * Gibbs declares `width` and `height` as top-level parameters in pixels, so a plan sends them
 * flat and as numbers; the export pane computes CSS strings. Both are accepted here, which is
 * what lets one callee serve the dispatcher and the UI without either guessing the other's shape.
 */
export function lidDimensions(flat) {
    const out = {};
    for (const key of LID_DIMENSION_KEYS) {
        if (flat[key] !== undefined) out[key] = cssLength(flat[key]);
    }
    return out;
}

/**
 * Fetch ProLife data for the given compound among binding site residues.
 * First determine binding site, and convert to PDB text for the residues.
 *
 * Dimensions may arrive nested under `parameters` or flat alongside `compound`; flat wins.
 * @param {{ compound: Compound, parameters?: { width?, height? }, width?, height? }} param0
 * @returns {{ html: string, error: string }}
 */
async function fetchProLifData({ compound, parameters: parametersIn, ...flatParams }={}) {
    const parameters = {
        ...lidDimensions(parametersIn || {}),
        ...lidDimensions(flatParams),
    };
    const pdbOptions = {
        bindingSiteDistance: 15,
        noPauseRedisplay: getPreference('PiPiAlgorithm') === 'prolif',
    };
    const { pdb, error, pdbObj } = await getProteinPdb(compound, pdbOptions);

    const noPdbMsg = 'Load a protein to see a ligand interaction diagram.'; // matches ExportProLIF

    const noPdb = !error && pdbObj.residues.length === 0;
    const haveError = error || pdb?.startsWith('Caught error processing text command request export-selection');

    const params = {
        width: parameters?.width ?? '100%',
        height: parameters?.height ?? '300px',
    };

    if (noPdb) return { error: noPdbMsg };
    if (haveError) {
        console.warn(`ProLIF LID prep error: ${error || pdb }`);
        return { error: 'This system failed to be prepared for a ligand interaction diagram.' };
    }
    // getMol2000() can come back empty if the server failed to deliver this compound's
    // structure metadata (e.g. an upstream binding-energy error aborted its case-files
    // response before CompoundMetadata was ever sent) -- getSDF() has no null guard, so
    // check explicitly rather than letting it throw past the error handling above.
    if (!compound.getMol2000()) {
        console.warn(`ProLIF LID prep error: compound ${compound.resSpec} has no structure (mol2000) data.`);
        return { error: 'This compound\'s structure data failed to load, so a ligand interaction diagram cannot be generated.' };
    }

    const sdf = compound.getSDF({ propInclusion: 'none' });

    // Call API and set LID html
    try {
        const result = await fetchLID(pdb, sdf, params);
        if (result.html) {
            return { html: result.html, pdbObj };
        } else {
            console.warn(`Error from LID service: ${result.error}`);
            return { error: 'The Ligand Interaction Diagram service failed to prepare the diagram for this structure.' };
        }
    } catch (errorObj) {
        console.warn(`Error communicating with LID service: ${errorObj.message}`);
        return { error: 'There was a problem communicating with the Ligand Interaction Diagram service.' };
    }
}

/**
 * Get a PDB string for a compounds binding site residues.
 *
 * `chains` restricts the protein to those chain ids and `ions` appends ion
 * atom groups as HETATM records; both are only honoured by the local method
 * (the server export takes a query instead), so pass `method: 'local'` with
 * them.
 * @param {Compound|CaseData} input
 * @param {{ method: string, bindingSiteDistance: number, noPauseRedisplay: boolean,
 *     chains?: string[], ions?: import('BMapsModel/atomgroups').AtomGroup[] }} options
 * @returns {{ pdb: string, error: string, pdbObj: PdbData } |
 *     Promise<{ pdb: string, error: string, pdbObj: PdbData }>
 * }
 */
async function getProteinPdb(input, options={}) {
    const {
        method: methodIn, bindingSiteDistance, noPauseRedisplay, chains, ions,
    } = options;
    const method = methodIn || getPreference('ProteinPdbMethod');

    if (method === 'local') {
        return localProteinPdb(input, { bindingSiteDistance, chains, ions });
    } else if (method === 'server') {
        if (chains || ions) {
            console.warn('GetProteinPdb: chains/ions are only supported by the local method; ignoring them');
        }
        return serverProteinPdb(input, { bindingSiteDistance, noPauseRedisplay });
    }
    return { pdb: '', error: `Invalid PDB method: ${method}` };
}

/**
 * Get PDB string for the compound's binding site residues, using BMaps internal logic.
 * @param {Compound|CaseData} input
 * @param {{ bindingSiteDistance: number, chains?: string[],
 *     ions?: import('BMapsModel/atomgroups').AtomGroup[] }} options
 * @returns {{ pdb: string, error: string, pdbObj: PdbData }}
 */
function localProteinPdb(input, { bindingSiteDistance, chains, ions }={}) {
    const { caseData } = App.getDataParents(input);
    let residues = caseData.getResidues();
    if (chains) {
        const wanted = new Set(chains);
        residues = residues.filter((r) => wanted.has(r.chain));
    }
    if (bindingSiteDistance != null && input instanceof Compound) {
        const compound = input;
        const bSite = BindingSite.calculate(App.Workspace, { compound }, { bindingSiteDistance });
        if (bSite) {
            residues = bSite.getComponentsInRange(residues);
        }
    }
    const pdbData = getPdbDataForResidues([...residues, ...(ions || [])]);
    return { pdb: pdbData.format(), error: '', pdbObj: pdbData };
}

/**
 * Get PDB string for the compound's binding site residues, using bfd-server export-selection.
 * @param {Compound|CaseData} input
 * @param {{ bindingSiteDistance: number, noPauseRedisplay: boolean }} options
 * @returns {Promise<{ pdb: string, error: string, pdbObj: PdbData }>}
 */
async function serverProteinPdb(input, { bindingSiteDistance, noPauseRedisplay }={}) {
    let pdb;
    let error;
    const { caseData } = App.getDataParents(input);
    let query = '(protein or rna or dna)';
    if (bindingSiteDistance != null && input instanceof Compound) {
        const compound = input;
        query += ` and within (${bindingSiteDistance} ${compound.resSpec}); roundup`;
    }
    const exportPromise = UserActions.ExportQueryToFormat(query, 'pdb', caseData);
    if (noPauseRedisplay) {
        // The redraw request below redraws the entire scene, including pi-pi stacks.
        // If ProLIF is used for detecting pi-pi interactions, redrawing the pi-pi stacks will
        // fetch ProLIF again, calling this function again for PDB data, redrawing the scene...
        // To avoid an infinite loop, don't pause & redraw when using ProLIF for pi-pi detection.
        // The atoms will briefly flash when they are selected, but better than infinite loop.
        console.warn('When using ProLIF pi-pi detection and bfd-server pdb generation, atoms flash during the LID request preparation.');
        ({ data: pdb, error } = await exportPromise);
        return { pdb, error, pdbObj: new PdbData(pdb) };
    } else {
        ({ data: pdb, error } = await withPausedRedisplay(exportPromise));
        // Pause redisplay to avoid flashing selected atoms.
        // Make sure the scene is redrawn afterwards, necessary because if the user
        // switches compounds with just the right timing, we could end up with a blank screen.
        // May occasionally cause slight delay, but since fetch is debounced, should be fine.
        EventBroker.publish('redrawSceneRequest');
        return { pdb, error, pdbObj: new PdbData(pdb) };
    }
}
